/*
  Collection infinite scroll (replaces the Infinite Scroll Pro app).

  Progressive enhancement: the server still renders the normal numbered
  pagination (real <a href="?page=N"> links), so crawlers and no-JS visitors
  can reach every page. When this script runs it hides that pagination and
  loads the next page's products as the shopper nears the end of the grid,
  using the Section Rendering API so only the product grid section is fetched.

  Markup it expects (rendered by sections/main-collection-product-grid.liquid):
    <collection-infinite-scroll data-section-id data-next-url data-current-page>
      <div class="collection-infinite__status" role="status"></div>
      <button class="collection-infinite__more" hidden>Load more</button>
    </collection-infinite-scroll>
*/
if (!customElements.get('collection-infinite-scroll')) {
  const STORE_KEY = 'collection-infinite-scroll';

  class CollectionInfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.nextUrl = this.dataset.nextUrl || '';
      this.sectionId = this.dataset.sectionId;
      this.page = parseInt(this.dataset.currentPage, 10) || 1;
      this.grid = document.getElementById('product-grid');
      this.collectionEl = this.closest('.collection');
      this.status = this.querySelector('.collection-infinite__status');
      this.button = this.querySelector('.collection-infinite__more');
      this.loading = false;

      if (!this.nextUrl || !this.grid || !this.sectionId || !('fetch' in window)) return;

      // Hide the numbered pagination only once infinite scroll is actually running.
      if (this.collectionEl) this.collectionEl.classList.add('collection--infinite');

      this.button.addEventListener('click', () => this.loadNext());
      this.onGridClick = this.rememberPosition.bind(this);
      this.grid.addEventListener('click', this.onGridClick);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
          },
          { rootMargin: '0px 0px 1200px 0px' }
        );
        this.observer.observe(this);
      } else {
        this.button.hidden = false;
      }

      this.restorePosition();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.grid && this.onGridClick) this.grid.removeEventListener('click', this.onGridClick);
    }

    async loadNext() {
      if (this.loading || !this.nextUrl) return false;
      this.loading = true;
      this.setAttribute('aria-busy', 'true');
      this.classList.add('is-loading');

      try {
        const url = new URL(this.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const items = Array.from(doc.querySelectorAll('#product-grid > li')).map((li) =>
          document.importNode(li, true)
        );
        items.forEach((li) => this.grid.appendChild(li));
        this.animate(items);

        const next = doc.querySelector('collection-infinite-scroll');
        this.nextUrl = next ? next.dataset.nextUrl || '' : '';
        this.page += 1;
        this.status.textContent = items.length ? `${items.length} more products loaded` : '';
        if (!this.nextUrl) this.finish();
        return true;
      } catch (error) {
        // Fall back to the normal numbered pagination rather than leaving shoppers stuck.
        this.fallback();
        return false;
      } finally {
        this.loading = false;
        this.removeAttribute('aria-busy');
        this.classList.remove('is-loading');
      }
    }

    // Run the theme's reveal-on-scroll animation on the new cards only.
    animate(items) {
      if (!items.length || typeof onIntersection !== 'function' || !('IntersectionObserver' in window)) {
        items.forEach((li) => li.classList.remove('scroll-trigger', 'animate--slide-in'));
        return;
      }
      const observer = new IntersectionObserver(onIntersection, { rootMargin: '0px 0px -50px 0px' });
      items.forEach((li) => {
        if (li.classList.contains('scroll-trigger')) observer.observe(li);
      });
    }

    finish() {
      if (this.observer) this.observer.disconnect();
      this.button.hidden = true;
    }

    fallback() {
      if (this.observer) this.observer.disconnect();
      this.nextUrl = '';
      this.button.hidden = true;
      if (this.collectionEl) this.collectionEl.classList.remove('collection--infinite');
    }

    // Back button support: remember how many pages were loaded when a product is opened.
    rememberPosition(event) {
      const link = event.target.closest('a[href]');
      if (!link || this.page <= 1) return;
      try {
        sessionStorage.setItem(
          STORE_KEY,
          JSON.stringify({ key: window.location.pathname + window.location.search, page: this.page, y: window.scrollY })
        );
      } catch (e) {}
    }

    async restorePosition() {
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
        sessionStorage.removeItem(STORE_KEY);
      } catch (e) {}
      if (!saved || saved.key !== window.location.pathname + window.location.search) return;
      const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (!nav || nav.type !== 'back_forward') return;
      while (this.page < saved.page && this.nextUrl) {
        if (!(await this.loadNext())) return;
      }
      window.scrollTo(0, saved.y);
    }
  }

  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
}
